import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Globe,
  Key,
  Award,
  FileText,
  Mail,
  Edit3,
  BarChart3, 
  Settings, 
  X,
  ChevronDown,
  ChevronRight,
  Layout,
  Shield,
  FlaskConical
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMsqAccreditations } from '../utils/msqAccreditationApi';
import { getMsqCaseStudies } from '../utils/msqCaseStudiesApi';
import { getAcademy } from '../utils/academyApi';
import { S3_BASE_URL } from '../config/api';

const Sidebar = ({ isOpen, onClose, currentPath }) => {
  const { user, userSites } = useAuth();
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [accreditations, setAccreditations] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [academyItems, setAcademyItems] = useState([]);

  // Handle userSites - it can be an array or an object
  const getSiteData = () => {
    if (!userSites) {
      // Try to get site data from user object as fallback
      if (user?.site_id || user?.site_name) {
        return {
          logo: user.logo || user.site_logo || null,
          site_name: user.site_name || user.name || 'Admin Panel'
        };
      }
      return { logo: null, site_name: 'Admin Panel' };
    }
    
    // If userSites is an array, get the first site
    if (Array.isArray(userSites) && userSites.length > 0) {
      const site = userSites[0];
      return {
        logo: site.logo || site.site_logo || site.site_meta?.logo || null,
        site_name: site.site_name || site.name || site.site_account || 'Admin Panel333'
      };
    }

    
    console.log('userSites', user);

    
    
    // If userSites is an object
    if (typeof userSites === 'object' && userSites !== null) {
      return {
        logo: userSites.logo || userSites.site_logo || userSites.site_meta?.logo || null,
        site_name: userSites.site_name || userSites.name || userSites.site_account || 'Admin Panel44'
      };
    }
    
    return { logo: null, site_name: 'Admin Panel' };
  };
 
  const siteData = getSiteData();

  // Fetch accreditations for sidebar
  useEffect(() => {
    const fetchAccreditations = async () => {
      try {
        const response = await getMsqAccreditations();
        if (response.success && response.data) {
          let accreditationData = response.data;
          
          // Handle different API response structures
          if (response.data && response.data.data) {
            accreditationData = response.data.data;
          } else if (response.data && response.data.accreditations) {
            accreditationData = response.data.accreditations;
          } else if (response.data && response.data.items) {
            accreditationData = response.data.items;
          } else if (response.data && response.data.results) {
            accreditationData = response.data.results;
          } else if (response.data && response.data.list) {
            accreditationData = response.data.list;
          } else if (response.data && response.data.records) {
            accreditationData = response.data.records;
          }
          
          if (Array.isArray(accreditationData)) {
            setAccreditations(accreditationData);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch accreditations for sidebar:', error);
        // Fallback to empty array if API fails
        setAccreditations([]);
      }
    };

    fetchAccreditations();
  }, []);

  // Fetch case studies for sidebar
  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await getMsqCaseStudies();
        if (response.success && response.data) {
          let caseStudyData = response.data;
          
          // Handle different API response structures
          if (response.data && response.data.data) {
            caseStudyData = response.data.data;
          } else if (response.data && response.data.case_studies) {
            caseStudyData = response.data.case_studies;
          } else if (response.data && response.data.items) {
            caseStudyData = response.data.items;
          } else if (response.data && response.data.results) {
            caseStudyData = response.data.results;
          } else if (response.data && response.data.list) {
            caseStudyData = response.data.list;
          } else if (response.data && response.data.records) {
            caseStudyData = response.data.records;
          }
          
          if (Array.isArray(caseStudyData)) {
            setCaseStudies(caseStudyData);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch case studies for sidebar:', error);
        // Fallback to empty array if API fails
        setCaseStudies([]);
      }
    };

    fetchCaseStudies();
  }, []);

  // Fetch academy items for sidebar
  useEffect(() => {
    const fetchAcademyItems = async () => {
      try {
        const response = await getAcademy();
        if (response.success && response.data) {
          let academyData = response.data;
          
          // Handle different API response structures
          if (response.data && response.data.data) {
            academyData = response.data.data;
          } else if (response.data && response.data.academy) {
            academyData = response.data.academy;
          } else if (response.data && response.data.items) {
            academyData = response.data.items;
          } else if (response.data && response.data.results) {
            academyData = response.data.results;
          } else if (response.data && response.data.list) {
            academyData = response.data.list;
          } else if (response.data && response.data.records) {
            academyData = response.data.records;
          }
          
          if (Array.isArray(academyData)) {
            setAcademyItems(academyData);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch academy items for sidebar:', error);
        // Fallback to empty array if API fails
        setAcademyItems([]);
      }
    };

    fetchAcademyItems();
  }, []);

  // Helper function to strip HTML tags from title
  const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Create accreditation subItems dynamically
  const accreditationSubItems = accreditations.map(accreditation => ({
    name: stripHtmlTags(accreditation.title) || `Accreditation ${accreditation.id}`,
    href: `/admin/accreditation/${accreditation.id}`,
    className: 'accreditation-item',
    module_number: accreditation.module_number || accreditation.certificate_number || null
  }));

  // Create case study subItems dynamically
  const caseStudySubItems = caseStudies.map(caseStudy => ({
    name: stripHtmlTags(caseStudy.title) || `Case Study ${caseStudy.id}`,
    href: `/admin/case-study/${caseStudy.id}`
  }));

  // Create academy subItems dynamically
  const academySubItems = academyItems.map(academyItem => ({
    name: stripHtmlTags(academyItem.title) || `Academy Item ${academyItem.id}`,
    href: `/admin/academy/${academyItem.id}`
  }));

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: Home },
    { name: 'MSQ Accreditation', href: '/admin/msq-accreditations', icon: Award, adminOnly: true },
    { name: 'MSQ Academy', href: '/admin/msq-academy', icon: Award, adminOnly: true },
    { name: 'MSQ Case Studies', href: '/admin/msq-case-studies', icon: FileText, adminOnly: true },
    { 
      name: 'MSQ Accreditation', 
      icon: Shield,
      userOnly: true,
      subItems: accreditationSubItems.length > 0 ? accreditationSubItems : [
        { name: 'No accreditations available', href: '/admin/msq-accreditations' }
      ]
    },
    { 
      name: 'MSQ Case Studies', 
      icon: FileText,
      userOnly: true,
      subItems: caseStudySubItems.length > 0 ? caseStudySubItems : [
        { name: 'No case studies available', href: '/admin/msq-case-studies' }
      ]
    },
    { 
      name: 'MSQ Academy', 
      icon: Shield,
      userOnly: true,
      subItems: academySubItems.length > 0 ? academySubItems : [
        { name: 'No academy available', href: '/admin/academy' }
      ]
    },
    { name: 'Site List', href: '/admin/sites', icon: Globe, adminOnly: true  },
    { name: 'User List', href: '/admin/users', icon: Users, adminOnly: true },
    { 
      name: 'Survey List', 
      icon: FileText,
      userOnly: true,
      subItems: [
        { name: 'Survey Responses', href: '/admin/surveys', icon: FileText },
        { name: 'Group Report', href: '/admin/group-report', icon: FileText },
      ]
    },
    { name: 'Access Codes', href: '/admin/access-codes', icon: Key, userOnly: true },
    { name: 'PDF Content Admin', href: '/admin/pdf-content', icon: Edit3 },
    { 
      name: 'Survey Invites', 
      icon: Mail, userOnly: true,
      subItems: [
        { name: 'Invite List', href: '/admin/invite-list', icon: Mail },
        { name: 'Invite Content', href: '/admin/invite-content', icon: Edit3 },    
      ]
    },
   
    //{ name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
    { 
      name: 'General Content Admin', 
      icon: Layout,
      userOnly: true,
      subItems: [
        { name: 'Confirmation Page', href: '/admin/confirmation-page' },
        { name: 'Email Templates', href: '/admin/email-templates' },
      ]
    },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
    //{ name: 'Test', href: '/admin/test', icon: FlaskConical },
  ];

  // Filter navigation items based on user role
  const filteredNavigation = useMemo(() => {
    return navigation.filter(item => {
      // If item is marked as adminOnly, only show it to admins
      if (item.adminOnly) {
        return user?.role === 'admin';
      }
      // If item is marked as userOnly, only show it to regular users (not admins)
      if (item.userOnly) {
        return user?.role === 'user';
      }
      // Show all other items to everyone
      return true;
    });
  }, [user?.role, accreditationSubItems, caseStudySubItems, academySubItems]);

  // Auto-expand submenu if current path matches any subitem
  useEffect(() => {
    if (!currentPath) return;
    
    const newOpenSubmenus = {};
    filteredNavigation.forEach(item => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(subItem => currentPath === subItem.href);
        if (hasActiveSubItem) {
          newOpenSubmenus[item.name] = true;
        }
      }
    });
    
    // Only update if there's a change
    if (Object.keys(newOpenSubmenus).length > 0) {
      setOpenSubmenus(prev => {
        const hasChanges = Object.keys(newOpenSubmenus).some(key => !prev[key]);
        return hasChanges ? { ...prev, ...newOpenSubmenus } : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const toggleSubmenu = (itemName) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-sidebar-bg pt-5 pb-4 overflow-y-auto">
          {/* Logo */}
          <div className="flex flex-col items-center flex-shrink-0">
            <div className="w-25 h-20 flex items-center justify-center">
              {siteData.logo ? (
                <img src={`${S3_BASE_URL}/${siteData.logo}`} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <div className="w-20 h-20 bg-gray-400 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">MSQ</span>
                </div>
              )}
            </div>
            {/*<h1 className="text-xl font-semibold text-sidebar-text text-center">{siteData.site_name}</h1>*/}
          </div>
          
          {/* Navigation */}
          <nav className="mt-8 flex-1 px-4 space-y-2">
            {filteredNavigation.map((item) => {
              if (item.subItems) {
                // Item with submenu
                const isSubmenuOpen = openSubmenus[item.name];
                const hasActiveSubItem = item.subItems.some(subItem => currentPath === subItem.href);
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`sidebar-item w-full ${hasActiveSubItem ? 'active' : ''}`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      <span className="flex-1 text-left">{item.name}</span>
                      {isSubmenuOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {isSubmenuOpen && (
                      <div className="ml-8 mt-2 space-y-1">
                        {item.subItems.map((subItem) => {
                          const isActive = currentPath === subItem.href;
                          const isAccreditation = item.name === 'MSQ Accreditation';
                          const isSurveyResponses = item.name === 'Survey List';
                          const isSurveyInvites = item.name === 'Survey Invites';
                          const isGeneralContentAdmin = item.name === 'General Content Admin';
                          const userFullAccess = user?.full_access || '1';
                 
                          // Disable link for accreditation items if user doesn't have full access
                          if (isAccreditation && userFullAccess == '1' && subItem.module_number >= 7) {
                            
                            return (
                              <span
                                key={subItem.name}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-not-allowed opacity-50 ${
                                  'text-sidebar-text'
                                } ${subItem.className || ''}`}
                              >
                                {subItem.name}
                              </span>
                            );
                          }

                          if (isSurveyResponses && userFullAccess <= '2') {
                            return (
                              <span
                                key={subItem.name}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-not-allowed opacity-50 ${
                                  'text-sidebar-text'
                                } ${subItem.className || ''}`}
                              >
                                {subItem.name}
                              </span>
                            );
                          }

                          if (isGeneralContentAdmin && userFullAccess <= '2') {
                            return (
                              <span
                                key={subItem.name}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-not-allowed opacity-50 ${
                                  'text-sidebar-text'
                                } ${subItem.className || ''}`}
                              >
                                {subItem.name}
                              </span>
                            );
                          }

                          if (isSurveyInvites && userFullAccess <= '2') {
                            return (
                              <span
                                key={subItem.name}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-not-allowed opacity-50 ${
                                  'text-sidebar-text'
                                } ${subItem.className || ''}`}
                              >
                                {subItem.name}
                              </span>
                            );
                          }

                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                                isActive 
                                  ? ' text-white' 
                                  : 'text-sidebar-text hover:bg-gray-700'
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular item without submenu
              const isActive = currentPath === item.href;
              const  isAccessCodes = item.name === 'Access Codes';
              const  isPdfContent = item.name === 'PDF Content';
              const isConfiguration = item.name === 'Configuration';
              const userFullAccess = user?.full_access || '1';

              if (isAccessCodes && userFullAccess <= '2') {
                return (
                  <Link
                  key={item.name}
                  className={`sidebar-item cursor-not-allowed opacity-50 ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
                    
                );
              }

              if (isPdfContent && userFullAccess <= '2') {
                return (
                  <Link
                  key={item.name}
                  className={`sidebar-item cursor-not-allowed opacity-50 ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
                    
                );
              }

              if (isConfiguration && userFullAccess <= '2') {
                return (
                  <Link
                  key={item.name}
                  className={`sidebar-item cursor-not-allowed opacity-50 ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
                    
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          {/* User Profile */}
          <div className="flex-shrink-0 px-4 py-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-sidebar-text">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-400">{user?.role || 'User'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-bg transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Header */}
          <div className="relative flex flex-col items-center px-4 py-4 border-b border-gray-700">
            <div className="flex flex-col items-center w-full">
              <div className="w-20 h-20 flex items-center justify-center mb-3">
                {siteData.logo ? (
                  <img src={`${S3_BASE_URL}/${siteData.logo}`} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <div className="w-20 h-20 bg-gray-400 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">MSQ</span>
                  </div>
                )}
              </div>
              {/*<h1 className="text-xl font-semibold text-sidebar-text text-center">{siteData.site_name}</h1>*/}
            </div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-sidebar-text hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Mobile Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {filteredNavigation.map((item) => {
              if (item.subItems) {
                // Item with submenu
                const isSubmenuOpen = openSubmenus[item.name];
                const hasActiveSubItem = item.subItems.some(subItem => currentPath === subItem.href);
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`sidebar-item w-full ${hasActiveSubItem ? 'active' : ''}`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      <span className="flex-1 text-left">{item.name}</span>
                      {isSubmenuOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {isSubmenuOpen && (
                      <div className="ml-8 mt-2 space-y-1">
                        {item.subItems.map((subItem) => {
                          const isActive = currentPath === subItem.href;
                          const isAccreditation = item.name === 'MSQ Accreditation';
                          const userFullAccess = user?.full_access || '1';
                          
                          // Disable link for accreditation items if user doesn't have full access
                          if (isAccreditation && userFullAccess !== '3') {
                            return (
                              <span
                                key={subItem.name}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-not-allowed opacity-50 ${
                                  'text-sidebar-text'
                                } ${subItem.className || ''}`}
                              >
                                {subItem.name}
                              </span>
                            );
                          }
                          
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                                isActive 
                                  ? ' text-white' 
                                  : 'text-sidebar-text hover:bg-gray-700'
                              }`}
                              onClick={onClose}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular item without submenu
              const isActive = currentPath === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={onClose}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          {/* Mobile User Profile 
          <div className="px-4 py-4 border-t border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-sidebar-text">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-400">{user?.role || 'User'}</p>
              </div>
            </div>
          </div>
            */}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
